import React, { useContext, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { X } from 'react-bootstrap-icons';

/**
 * Sticky CTA bar that appears at bottom of page on scroll
 * Shows for non-logged-in users to encourage sign-up
 * @param {Function} onCtaClick - Optional callback when CTA is clicked
 * @param {Function} onCtaHover - Optional callback when CTA is hovered
 * @param {string} ctaText - Optional CTA button text (default: "Enforce Your Rules")
 * @param {string} ctaLink - Optional CTA link (default: "/register")
 */
const StickyCTA = ({
  onCtaClick,
  onCtaHover,
  ctaText = 'Enforce Your Rules',
  ctaLink = '/register',
  copy = 'Enforce your rules. 14-day free trial.',
}) => {
  const { user } = useContext(AuthContext);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Don't show if user is logged in
    if (user) {
      setIsVisible(false);
      return;
    }

    // Check if user has dismissed it in this session
    const dismissed = sessionStorage.getItem('stickyCTA_dismissed');
    if (dismissed === 'true') {
      setIsDismissed(true);
      return;
    }

    // Show after scrolling down 300px
    const handleScroll = () => {
      if (window.scrollY > 300 && !isDismissed) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [user, isDismissed]);

  const handleDismiss = () => {
    setIsVisible(false);
    setIsDismissed(true);
    sessionStorage.setItem('stickyCTA_dismissed', 'true');
  };

  if (user || isDismissed) {
    return null;
  }

  return (
    <div
      className={`landing-sticky-cta${isVisible ? ' landing-sticky-cta--visible' : ''}`}
      aria-hidden={!isVisible}
    >
      <div className="landing-sticky-cta__content">
        <p className="landing-sticky-cta__copy">
          {copy}
        </p>
        <Link
          to={ctaLink}
          tabIndex={isVisible ? undefined : -1}
          onClick={() => {
            if (onCtaClick) {
              onCtaClick();
            }
          }}
          onMouseEnter={() => {
            if (onCtaHover) {
              onCtaHover();
            }
          }}
          className="landing-sticky-cta__link"
        >
          {ctaText}
        </Link>
      </div>
      <button
        type="button"
        className="landing-sticky-cta__dismiss"
        onClick={handleDismiss}
        tabIndex={isVisible ? undefined : -1}
        aria-label="Dismiss"
      >
        <X size={20} />
      </button>
    </div>
  );
};

export default StickyCTA;
